import { lazy, Suspense, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import Loadable from "./components/feature/Loadable/Loadable";
import { GlobalLoading } from "./hooks/useGlobalLoading";

import GuestLayout from "./layouts/GuestLayout";
import AuthLayout from "./layouts/AuthLayout";
import useAuth from "./hooks/useAuth";
import LoaderSpinner from "./components/common/LoadSpinner/LoadSpinner";
import GeminiChat from "./components/feature/GeminiChat/GeminiChat";
import { GeminiChatProvider } from "./contexts/GeminiContext";

const Home = Loadable(lazy(() => import("@/page/Guest/Home/Home")));
const Dashboard = Loadable(
	lazy(() => import("@/page/Authenticated/Dashboard/Dashboard"))
);
const CompanySettings = Loadable(
	lazy(() => import("@/page/Authenticated/CompanySettings/CompanySettings"))
);
const Login = Loadable(lazy(() => import("@/page/Guest/Login/Login")));
const ChatAi = Loadable(
	lazy(() => import("@/page/Authenticated/ChatAI/ChatAi"))
);
const TimeSheet = Loadable(
	lazy(() => import("@/page/Authenticated/TimeSheet/TimeSheet"))
);
const LeaveManagement = Loadable(
	lazy(() => import("@/page/Authenticated/LeaveManagement/LeaveManagement"))
);
const TeamDirectory = Loadable(
	lazy(() => import("@/page/Authenticated/TeamDirectory/TeamDirectory"))
);
const StaffProfile = Loadable(
	lazy(() => import("@/page/Authenticated/TeamDirectory/StaffProfile"))
);

const CompanyResource = Loadable(
	lazy(() => import("@/page/Authenticated/CompanyResource/CompanyResource"))
);
const Profile = Loadable(
	lazy(() => import("@/page/Authenticated/Profile/Profile"))
);

const ChangePassword = Loadable(
	lazy(() => import("@/page/Authenticated/ChangePassword/ChangePassword"))
);

const Events = Loadable(
	lazy(() => import("@/page/Authenticated/Events/Events"))
);

const ActivateAccount = Loadable(
	lazy(() => import("@/page/Guest/ActivateAccount/ActivateAccount"))
);

const PrivacyPolicy = Loadable(
	lazy(() => import("@/page/Authenticated/PrivacyPolicy/PrivacyPolicy"))
);
const TermsAndCondition = Loadable(
	lazy(() => import("@/page/Authenticated/TermsAndCondition/TermsAndCondition"))
);

const Router = () => {
	const { isLoading, isAuthenticated, accessToken, user } = useAuth();
	const [showLoader, setShowLoader] = useState<boolean>(false);

	return (
		<GlobalLoading.Provider value={{ showLoader, setShowLoader }}>
			<GeminiChatProvider>
				{isAuthenticated && <GeminiChat />}
				{showLoader && (
					<div className="flex absolute left-0 top-3 justify-center items-center fixed h-full w-full z-50 bg-black bg-opacity-50">
						<LoaderSpinner className="w-10 h-20" />
					</div>
				)}
				<Suspense fallback={null}>
					<BrowserRouter>
						<Routes>
							{isLoading ? (
								<Route
									path="*"
									element={
										<div className="flex absolute left-0 top-3 justify-center items-center fixed h-full w-full">
											<LoaderSpinner className="w-10 h-20" />
										</div>
									}
								/>
							) : (
								<>
									<Route path="/privacy-policy" element={<PrivacyPolicy />} />
									<Route
										path="/terms-and-condition"
										element={<TermsAndCondition />}
									/>
									{isAuthenticated && accessToken && user && (
										<>
											<Route path="/" element={<AuthLayout />}>
												<Route
													index
													path="/"
													element={<Navigate to="/dashboard" />}
												/>
												<Route path="/dashboard" element={<Dashboard />} />
												<Route path="/attendance" element={<TimeSheet />} />
												<Route
													path="/leave-management"
													element={<LeaveManagement />}
												/>
												<Route
													path="/team-directory"
													element={<TeamDirectory />}
												/>
												<Route path="/team-calendar" element={<Events />} />
												<Route
													path="/team-directory/:id"
													element={<StaffProfile />}
												/>
												<Route
													path="/company-resources"
													element={<CompanyResource />}
												/>
												<Route
													path="/company-settings"
													element={<CompanySettings />}
												/>
												<Route path="/chat-ai" element={<ChatAi />} />
												<Route path="/profile" element={<Profile />} />
												<Route
													path="/change-password"
													element={<ChangePassword />}
												/>
											</Route>
										</>
									)}
									{!isAuthenticated && !accessToken && !user && (
										<>
											<Route path="/" element={<GuestLayout />}>
												<Route index path="/" element={<Login />} />
												<Route index path="/login" element={<Login />} />
												<Route path="/components" element={<Home />} />
												<Route path="/activate" element={<ActivateAccount />} />
											</Route>
										</>
									)}
								</>
							)}
						</Routes>
					</BrowserRouter>
				</Suspense>
			</GeminiChatProvider>
		</GlobalLoading.Provider>
	);
};

export default Router;
